/**
 * ap5-a5: the merge tags the accounts document substitutes when it prints a note's wording. The note
 * editors showed a "Capium Tags" heading with nothing under it, so the tags existed on the API but a
 * preparer had no way to discover or insert one.
 *
 * Keep in step with Cap3MergeTags.Catalogue in the API, which fills every one of these wherever the accounts print
 * note wording (director's report, accountant's report, accounting policies and the additional notes); its tests fail
 * if a tag listed there is printed literally. A tag that is not in that list is printed literally so the preparer can
 * see it is wrong.
 */
export interface CapiumTag {
  tag: string;
  label: string;
}

export interface CapiumTagGroup {
  category: string;
  tags: CapiumTag[];
}

/**
 * AP parity: the groups, tags and labels of the Capium 3.0 note popup (Accountant, Periods, Company information,
 * Directors details, Financial details, Pages - 24 tags), in its order. Three tags the 3.0 popup did not offer are
 * kept because saved wording already uses them: [period end date], [registered office address] and
 * [director_acknowledge_s]. Every editor shows this one list for a client type (capiumTagGroupsFor), so no screen can offer fewer tags than another.
 */
export const CAPIUM_TAG_GROUPS: CapiumTagGroup[] = [
  {
    category: 'Accountant',
    tags: [
      { tag: '[accountant_engagement_date]', label: 'Accountant engagement date' },
      { tag: '[accountant_name]', label: 'Name of Accountant' },
    ],
  },
  {
    category: 'Periods',
    tags: [
      { tag: '[period_start_date]', label: 'Period start date (dd MMMM yyyy)' },
      { tag: '[date_period_end]', label: 'Period end date (dd MMMM yyyy)' },
      { tag: '[period]', label: 'period/year' },
      { tag: '[accounting_period]', label: 'year/period ended (dd MMMM yyyy)' },
      { tag: '[period_end_year]', label: 'period end year' },
      { tag: '[period end date]', label: 'Period end date (dd-MMM-yyyy)' },
    ],
  },
  {
    category: 'Company information',
    tags: [
      { tag: '[Company name]', label: 'Company name' },
      { tag: '[registration_number]', label: 'Registration number' },
      { tag: '[company_type]', label: 'Company Type' },
      { tag: '[company_registered_in]', label: 'Company Registered In' },
      { tag: '[registered office address]', label: 'Registered office address' },
    ],
  },
  {
    category: 'Directors details',
    tags: [
      { tag: '[director_signatory]', label: 'Director signatory (Directors report)' },
      { tag: '[director_s]', label: 'director/directors' },
      { tag: '[director_is_are]', label: 'director is/directors are' },
      { tag: '[has_have]', label: 'has/have' },
      { tag: '[him_them]', label: 'him/them' },
      { tag: '[her_them]', label: 'her/them' },
      { tag: '[was_were]', label: 'was/were' },
      { tag: '[display_directors]', label: 'Display Directors' },
      { tag: '[director_present_s]', label: 'The director/directors present/presents his/their' },
      { tag: '[director_acknowledge_s]', label: 'Director acknowledges / directors acknowledge' },
    ],
  },
  {
    category: 'Financial details',
    tags: [
      { tag: '[profit_loss]', label: 'Profit/Loss' },
      { tag: '[profit_figure]', label: 'Profit figure from P&L' },
    ],
  },
  {
    category: 'Pages',
    tags: [
      { tag: '[balance_sheet_pages]', label: 'Balancesheet pages' },
      { tag: '[selected_report_pages]', label: 'Selected report pages' },
    ],
  },
];

/**
 * AP Parity: Email templates use the merge tags of 3.0 - [company_name], [practice_name], [period_from], [period_to] -
 * and a few more. BUG-017: the API (EmailTemplateTags.Panel) fills every tag listed here, in any case, and its tests
 * read this list from this file, so a tag added here without an API value fails there.
 */
export const EMAIL_TEMPLATE_TAG_GROUPS: CapiumTagGroup[] = [
  {
    category: 'Practice & Company',
    tags: [
      { tag: '[company_name]', label: 'Company name' },
      { tag: '[practice_name]', label: 'Practice name' },
      { tag: '[registered office address]', label: 'Registered office address' },
      { tag: '[contact_name]', label: 'Contact name' },
    ],
  },
  {
    category: 'Periods',
    tags: [
      { tag: '[period_from]', label: 'Period from/start date' },
      { tag: '[period_to]', label: 'Period to/end date' },
      { tag: '[period_start_date]', label: 'Period start date (dd MMMM yyyy)' },
      { tag: '[date_period_end]', label: 'Period end date (dd MMMM yyyy)' },
      { tag: '[period end date]', label: 'Period end date (dd-MMM-yyyy)' },
      { tag: '[accounting_period]', label: 'Accounting period' },
      { tag: '[period]', label: 'Period or year' },
      { tag: '[due_date]', label: 'Accounts due date' },
    ],
  },
  {
    category: 'Directors details',
    tags: [
      { tag: '[director_s]', label: 'Director / directors' },
      { tag: '[director_acknowledge_s]', label: 'Director acknowledges / directors acknowledge' },
    ],
  },
];

/** Flattened from the groups so the two views cannot drift apart. */
export const CAPIUM_TAGS: CapiumTag[] = CAPIUM_TAG_GROUPS.flatMap((group) => group.tags);

/**
 * BUG-072 (round 2): an LLP has members, not directors, so its Capium Tags panel reads "member(s)" wherever a group or
 * label says "director(s)" - the tag codes themselves (e.g. `[director_s]`) stay exactly as written; the report already
 * resolves them to the LLP's own wording (Cap3MergeTags.Catalogue).
 */
function llpLabel(text: string): string {
  return text
    .replace(/\bDirectors\b/g, 'Members')
    .replace(/\bdirectors\b/g, 'members')
    .replace(/\bDirector\b/g, 'Member')
    .replace(/\bdirector\b/g, 'member');
}

export function llpTagGroups(groups: CapiumTagGroup[]): CapiumTagGroup[] {
  return groups.map((group) => ({
    category: llpLabel(group.category),
    tags: group.tags.map((item) => ({ ...item, label: llpLabel(item.label) })),
  }));
}

/**
 * APT312 (the client: "Update Capium Tags based on the client type. Remove Director-related tags from LLP, Sole Trader and Partnership clients"):
 * the tags a note editor offers follow the client's type (ClientTypes: 0 Limited company, 1 Sole trader, 2 Partnership, 3 LLP; null while it
 * is not known yet = the limited-company list). Only the panel changes: the API fills every tag in the catalogue for any client, so wording a
 * note already holds keeps printing exactly as before.
 * - Limited company: every group.
 * - LLP: members, not directors (llpTagGroups); the Company information tags stay, as an LLP is registered at Companies House.
 * - Sole trader and partnership: no Companies Act wording - no Directors details, and no Company information beyond the business name
 *   (`[Company name]` is what the default wording already uses for the business, so it stays, labelled for what it is), plus the
 *   business address and the proprietor / partner names (APT312, see businessTags).
 */
/**
 * APT312 (Business Info / Business Contacts): what a sole trader's or partnership's note can say about the business - its name and
 * address from Business Info, and the proprietor (sole trader) or the partners (partnership) from Business Contacts.
 * The API fills them for every client (Cap3MergeTags.Catalogue); only these two client types are offered them.
 */
function businessTags(clientType: 1 | 2): CapiumTag[] {
  return [
    { tag: '[Company name]', label: 'Business name' },
    { tag: '[business_address]', label: 'Business address' },
    clientType === 1
      ? { tag: '[proprietor_name]', label: 'Proprietor name' }
      : { tag: '[partner_names]', label: 'Partner names' },
  ];
}

export function capiumTagGroupsFor(clientType: number | null | undefined): CapiumTagGroup[] {
  if (clientType === 3) {
    return llpTagGroups(CAPIUM_TAG_GROUPS);
  }
  if (clientType === 1 || clientType === 2) {
    const businessType: 1 | 2 = clientType;
    return CAPIUM_TAG_GROUPS.flatMap((group) => {
      if (group.category === 'Directors details') return [];
      if (group.category === 'Company information') {
        return [{ category: 'Business information', tags: businessTags(businessType) }];
      }
      return [group];
    });
  }
  return CAPIUM_TAG_GROUPS;
}

/** Appends a tag to rich-text wording. The editors are contenteditable, so the tag lands at the end. */
export function appendCapiumTag(content: string | null | undefined, tag: string): string {
  const text = content ?? '';
  return text.length === 0 || /\s$/.test(text) ? `${text}${tag}` : `${text} ${tag}`;
}
